import type { DiscordSDK } from '@discord/embedded-app-sdk';
import type { PublicUserState } from '@casino/shared';
import { apiCall, setReauthenticator } from '../api/client.js';
import { getEnv } from '../env.js';
import { applyUserState, setSession } from '../state/store.js';

/**
 * Chaine d'authentification complete.
 *
 *   sdk.ready()                (deja fait dans bootDiscord)
 *   sdk.commands.authorize     -> code OAuth2
 *   POST /api/token { code }   -> { sessionToken, expiresAt, user }
 *                                 (echange serveur : le CLIENT_SECRET ne quitte
 *                                  jamais le VPS)
 *   sessionToken en memoire    -> Authorization: Bearer sur toutes les routes
 *
 * On s'arrete la : `sdk.commands.authenticate` ne sert qu'a obtenir un profil
 * Discord cote client, or le profil nous vient deja de `/api/token`, verifie
 * cote serveur. Une source de verite, pas deux.
 *
 * Il n'y a plus aucune session tierce a etablir : le JWT emis par `/api/token`
 * EST la session, et il ne quitte jamais la memoire de l'onglet.
 */

export type AuthStage = 'authorizing' | 'exchanging' | 'done';

export interface AuthResult {
  readonly user: PublicUserState;
  /** Epoch ms d'expiration du jeton de session. */
  readonly expiresAt: number;
}

export class AuthError extends Error {
  readonly stage: AuthStage;

  constructor(stage: AuthStage, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'AuthError';
    this.stage = stage;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Le SDK Discord ne rejette pas avec une `Error` mais avec l'objet RPC brut,
 * typiquement `{ code, message }`. Un `String(cause)` dessus produit
 * `[object Object]`, ce qui efface la seule information utile au moment ou on
 * en a le plus besoin. On extrait donc explicitement message et code.
 */
function describe(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  if (typeof cause === 'string') return cause;

  if (isRecord(cause)) {
    const parts: string[] = [];
    const message = cause['message'];
    if (typeof message === 'string' && message.length > 0) parts.push(message);
    const code = cause['code'];
    if (typeof code === 'number' || typeof code === 'string') parts.push(`code ${String(code)}`);
    if (parts.length > 0) return parts.join(' — ');
    try {
      return JSON.stringify(cause);
    } catch {
      return 'erreur non sérialisable';
    }
  }

  return String(cause);
}

export async function authenticate(
  sdk: DiscordSDK,
  onStage: (stage: AuthStage) => void = () => {},
): Promise<AuthResult> {
  onStage('authorizing');

  // `prompt` est volontairement OMIS. Le SDK ouvre alors la modale
  // d'autorisation quand le joueur n'a pas encore accorde les scopes, et la
  // saute quand il les a deja. Passer `prompt: 'none'` interdit cette modale :
  // l'activite devient inutilisable pour tout nouveau joueur, qui n'a par
  // definition jamais rien autorise. Ne le remets pas.
  let code: string;
  try {
    const authorized = await sdk.commands.authorize({
      client_id: getEnv().discordClientId,
      response_type: 'code',
      scope: ['identify'],
    });
    code = authorized.code;
  } catch (cause) {
    throw new AuthError(
      'authorizing',
      `Discord a refusé l'autorisation : ${describe(cause)}`,
      { cause },
    );
  }

  onStage('exchanging');
  const token = await apiCall('/api/token', { code }, { authenticated: false });

  setSession(token.sessionToken, token.expiresAt);
  applyUserState(token.user);

  // Si un appel ulterieur se prend un 401 (jeton expire, serveur redemarre),
  // le client HTTP rejoue exactement cette chaine, une seule fois.
  setReauthenticator(async () => {
    await authenticate(sdk);
  });

  onStage('done');
  return { user: token.user, expiresAt: token.expiresAt };
}
